/**
 * Turns `?group=…&list=…` into the app's stored selection, then removes it from the URL.
 *
 * This is how a tapped notification opens the right list. The SPA has no route per list —
 * which list is showing is a *preference* in localStorage, resolved against whatever lists
 * the group actually has (see `useSelectedGroup` and `useSelectedList`) — so a notification
 * cannot simply link to a path. It carries the two ids as query parameters and this writes
 * them into the same keys the switchers read.
 *
 * **It runs before the first render**, from `main.tsx`, for the reason `startInstallOffer`
 * does: both switchers seed their `useState` from localStorage on mount, and a value
 * written after that point would be ignored until something else remounted them. Writing
 * the preference rather than lifting state also means nothing else in the app has to know
 * this feature exists — no prop, no context, no effect keeping two copies in step.
 *
 * The ids are validated before they are stored. Not because a bad one would be dangerous —
 * both hooks fall back to the first available group or list when a stored id resolves to
 * nothing — but because storing junk turns a self-healing miss into a value that sits in
 * localStorage looking like a real preference.
 */

const SELECTED_GROUP_KEY = 'bnb.selectedGroupId';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function selectedListKey(groupId: string): string {
  return `bnb.selectedListId:${groupId}`;
}

export function applyOpenTarget(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const params = new URLSearchParams(window.location.search);

  const group = params.get('group');
  const list = params.get('list');

  if (group === null && list === null) {
    return;
  }

  try {
    if (group !== null && UUID.test(group)) {
      localStorage.setItem(SELECTED_GROUP_KEY, group);

      // The list key is scoped by group, so a list can only be honoured alongside one.
      // A notification always carries both; a hand-edited URL might not.
      if (list !== null && UUID.test(list)) {
        localStorage.setItem(selectedListKey(group), list);
      }
    }
  } catch {
    // Storage denied, as in a locked-down private window. The app still opens; it just
    // opens on whatever list it was already showing.
  }

  // Strip them either way, including when they were malformed. Leaving them in the address
  // bar means a reload re-applies the selection, which quietly overrides the switcher for
  // the rest of the visit — and on an installed app the URL is invisible, so there would be
  // nothing on screen to explain why.
  params.delete('group');
  params.delete('list');

  const query = params.toString();

  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${query.length > 0 ? `?${query}` : ''}${window.location.hash}`,
  );
}
